import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { toast } from "@grandvisionhq/common";
import { Util } from "@grandvisionhq/forms";
import { Alert } from "@grandvisionhq/iris-ui";
import { useFeatureToggles, useIntl } from "@grandvisionhq/state";
import { useTrackPageView } from "@grandvisionhq/svc-tealium/events";
import { DateUtils } from "@grandvisionhq/utils";
import { useAppointmentForm } from "../../../../../context/AppointmentFormContext/v3/context/AppointmentFormContext/AppointmentFormContext.js";
import { timeSelectionErrorEvent, bookAppointmentWhoStepScreenEvent } from "../../../../../data-layer/v3/data-layer/actions.js";
import { useBookAppointmentById } from "../../../../../hooks/api/v3/hooks/api/use-book-appointment-by-id.js";
import { useUser } from "../../../../../hooks/api/v3/hooks/api/use-user.js";
import { useAuthToken } from "../../../../../hooks/v3/hooks/use-auth-token.js";
import useDataLayerInput from "../../../../../hooks/v3/hooks/use-data-layer-input.js";
import { useAppointmentContext } from "../../../../../pages/BookAppointment/context/v3/pages/BookAppointment/context/AppointmentContext.js";
import { useRoutingContext } from "../../../../../pages/BookAppointment/context/RoutingContext/v3/pages/BookAppointment/context/RoutingContext/context.js";
import { setStoredAppointment } from "../../../../../utils/v3/utils/book-appointment-storage.js";
import AppointmentPickDifferentTimeSlotContainer from "../../../../AppointmentPickDifferentTimeSlot/v3/components/AppointmentPickDifferentTimeSlot/AppointmentPickDifferentTimeSlotContainer.js";
import { AppointmentCustomerForm } from "./AppointmentCustomerForm.js";
import { useScrollToField } from "../../../hooks/v3/components/AppointmentCustomerForm/hooks/use-scroll-to-field.js";
import { BookAppointmentFormLayout } from "../../../../../types/v3/types/Forms.js";
import useTimeSlotsById from "../../../../../hooks/api/v3/hooks/api/use-time-slots-by-id.js";
import { extractBookAppointmentError } from "../../../../../utils/v3/utils/extract-book-appointment-error.js";
const AppointmentCustomerFormContainer = () => {
  const { Form } = useAppointmentForm();
  const [userQuery, userQueryState] = useUser();
  const [, authTokenState] = useAuthToken();
  const { isMyAccountEnabled } = useFeatureToggles();
  const intl = useIntl();
  const { getLabel } = intl;
  const [{ goTo }, { queryParams }] = useRoutingContext();
  const [{ setAppointment }, { type, store, timeSlot, tryInStoreProductSkus }] = useAppointmentContext();
  const layout = type?.ageGroup === "CHILD" ? BookAppointmentFormLayout.CHILD : BookAppointmentFormLayout.DEFAULT;
  const [bookAppointment, bookAppointmentState] = useBookAppointmentById();
  const [errorLabelKey, setErrorLabelKey] = React.useState();
  const [redirecting, setRedirecting] = React.useState(false);
  const [firstInvalidField, setFirstInvalidField] = React.useState("");
  const [initialFormState, setInitialState] = React.useState();
  const [whoStepTimeSlot, setWhoStepTimeSlot] = React.useState(null);
  const [getTimeSlots, timeSlots] = useTimeSlotsById();
  const bookAppointmentError = extractBookAppointmentError(bookAppointmentState.data.failures);
  const showPickDifferentTimeSlot = bookAppointmentError === "AlreadyBookedOrNotAvailableFailure";
  const disableSubmitForTimeSlot = showPickDifferentTimeSlot ? whoStepTimeSlot ? false : true : false;
  const authToken = authTokenState.data;
  const disableSubmit = bookAppointmentState.loading || disableSubmitForTimeSlot;
  const loading = authTokenState.loading || userQueryState.loading;
  const loggedIn = isMyAccountEnabled && Boolean(authToken);
  const toCamelCase = (str) => str.toLowerCase().replace(/[-_][a-z]/g, (group) => group.slice(-1).toUpperCase());
  const errorLabelSuffix = errorLabelKey ? toCamelCase(errorLabelKey) : "";
  const alertTitle = !showPickDifferentTimeSlot ? getLabel(`appointments.component.bookAppointmentForm.alert.title.${errorLabelSuffix}`) : void 0;
  const alertMessage = getLabel(
    `appointments.component.bookAppointmentForm.alert.message.${errorLabelSuffix}`
  );
  const dataLayerInput = useDataLayerInput();
  React.useEffect(() => {
    if (showPickDifferentTimeSlot) {
      const dataLayerEvent = timeSelectionErrorEvent(dataLayerInput);
      DataLayer.pushEvent(dataLayerEvent);
    }
  }, [dataLayerInput, showPickDifferentTimeSlot]);
  React.useEffect(() => {
    if (!userQueryState.data) {
      return;
    }
    const options = {
      intl,
      layout
    };
    setInitialState(Form?.fromDataToState(userQueryState.data, options));
  }, [Form, intl, layout, userQueryState.data]);
  const onSubmit = React.useCallback(
    (state) => {
      if (!state.formValid) {
        setInitialState(Util.touchComposedFormFields(state.formState));
        toast.failed("appointments.component.bookAppointmentForm.validationError");
        setFirstInvalidField(
          Util.getFirstInvalidField(state.formState, Form?.getFormGroups({ layout }) ?? [])
        );
        return;
      }
      if (Form) {
        const formData = Form.fromStateToData(
          state.formState,
          {
            intl,
            layout
          },
          { loggedIn }
        );
        bookAppointment(authToken, formData);
      }
    },
    [Form, intl, layout, authToken, bookAppointment, loggedIn]
  );
  useScrollToField(firstInvalidField, () => setFirstInvalidField(""));
  React.useEffect(() => {
    if (authToken) {
      userQuery(authToken);
    }
  }, [authToken, userQuery]);
  React.useEffect(() => {
    const bookedAppointment = bookAppointmentState.data.appointment;
    if (!bookedAppointment || redirecting) {
      return;
    }
    setRedirecting(true);
    setAppointment(bookedAppointment);
    setStoredAppointment(bookedAppointment);
    const { typeId, storeId, start, end } = queryParams;
    goTo("confirmation", {
      typeId,
      storeId,
      start,
      end,
      appointmentId: bookedAppointment.id,
      tryInStoreProductSkus
    });
  }, [bookAppointmentState]);
  React.useEffect(() => {
    const { typeId, storeId, start, end } = queryParams;
    const hasEqualTimeSlotFromUrl = timeSlot && start === `${timeSlot.from.getTime()}` && end === `${timeSlot.until.getTime()}`;
    const canPushEvent = store && type && hasEqualTimeSlotFromUrl && Number(storeId) === store.code && typeId === type.id;
    if (canPushEvent) {
      const dataLayerEvent = bookAppointmentWhoStepScreenEvent(dataLayerInput);
      DataLayer.pushEvent(dataLayerEvent);
    }
  }, [queryParams, store, timeSlot, type, dataLayerInput]);
  React.useEffect(() => {
    if (bookAppointmentState.error) {
      setErrorLabelKey(
        "INTERNAL_SERVER_ERROR"
        /* INTERNAL_SERVER_ERROR */
      );
      return;
    }
    const bookAppointmentFailures = bookAppointmentState.data.failures;
    if (bookAppointmentFailures && bookAppointmentFailures.length > 0) {
      const failureType = bookAppointmentFailures[0]?.__typename;
      switch (failureType) {
        case "AlreadyBookedOrNotAvailableFailure":
          setErrorLabelKey(
            "APPOINTMENT_ALREADY_BOOKED_OR_NOT_AVAILABLE"
            /* APPOINTMENT_ALREADY_BOOKED_OR_NOT_AVAILABLE */
          );
          break;
        case "AppointmentCreationFailure":
          setErrorLabelKey(
            "APPOINTMENT_CREATION_ERROR"
            /* APPOINTMENT_CREATION_ERROR */
          );
          break;
        case "AppointmentMaxExceededFailure":
          setErrorLabelKey(
            "APPOINTMENT_MAX_EXCEEDED"
            /* APPOINTMENT_MAX_EXCEEDED */
          );
          break;
        case "ExternalServerErrorFailure":
          setErrorLabelKey(
            "EXTERNAL_SERVER_ERROR"
            /* EXTERNAL_SERVER_ERROR */
          );
          break;
        default:
          setErrorLabelKey(
            "INTERNAL_SERVER_ERROR"
            /* INTERNAL_SERVER_ERROR */
          );
      }
      return;
    }
    setErrorLabelKey(void 0);
  }, [bookAppointmentError, bookAppointmentState.data.failures, bookAppointmentState.error]);
  React.useEffect(() => {
    if (!showPickDifferentTimeSlot && whoStepTimeSlot) {
      setWhoStepTimeSlot(null);
    }
    const globalStoreId = store?.globalStoreId;
    const appointmentTypeId = type?.id;
    const date = timeSlot?.from;
    if (!date || globalStoreId === void 0 || appointmentTypeId === void 0) {
      return;
    }
    getTimeSlots({
      variables: {
        globalStoreId,
        appointmentTypeId,
        date: DateUtils.toLocalDate(date)
      }
    });
  }, [showPickDifferentTimeSlot]);
  const aabWhoPageView = React.useCallback(
    () => new Promise((resolve) => {
      if (!store || !type) {
        return;
      }
      resolve({
        id: "VirtualPage-View",
        Page_Type: "Info",
        Page_Section1: "Exam",
        Page_Section2: "PersonalInfo",
        Exam_Fitting: type.name,
        Store_Search_StoreId: store.globalStoreId
      });
    }),
    [store, type]
  );
  useTrackPageView(aabWhoPageView);
  return /* @__PURE__ */ React.createElement(
    AppointmentCustomerForm,
    {
      loading,
      loggedIn,
      Form,
      layout,
      initialFormState,
      onSubmit,
      submitting: bookAppointmentState.loading,
      disableSubmit,
      title: getLabel("appointments.component.bookAppointmentForm.title"),
      beforeSubmitSlot: showPickDifferentTimeSlot ? /* @__PURE__ */ React.createElement(
        AppointmentPickDifferentTimeSlotContainer,
        {
          onSelectTimeSlot: setWhoStepTimeSlot,
          selectedTimeSlot: whoStepTimeSlot,
          timeSlots: timeSlots.filtered.included,
          loading: timeSlots.loading
        }
      ) : void 0,
      formAlert: errorLabelKey && /* @__PURE__ */ React.createElement(Alert, { variant: "warning", title: alertTitle, className: "alert-m-default" }, alertMessage)
    }
  );
};
export {
  AppointmentCustomerFormContainer,
  AppointmentCustomerFormContainer as default
};
//# sourceMappingURL=AppointmentCustomerFormContainer.js.map
